import * as Sentry from '@sentry/react'
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  OAuthCancelledError,
  type OAuthProvider,
  signInWithOAuthProvider,
} from '../helpers/oauth'

/**
 * Drives the Apple/Google sign-in buttons on the auth screens.
 *
 * Tracks which provider is currently mid-flight (for spinner + disabled
 * state), surfaces a translated error message on failure, and ignores user
 * cancellations silently. Callers navigate after onSuccess.
 *
 * In-flight state is stored on a ref as well as React state so a second tap
 * before the next render cannot start an overlapping Apple/Google popup.
 */
export default function useSocialAuth({
  onSuccess,
}: {
  onSuccess: (userId: string | undefined) => void | Promise<void>
}) {
  const { t } = useTranslation()
  const [busyProvider, setBusyProvider] = useState<OAuthProvider | null>(null)
  const busyProviderRef = useRef<OAuthProvider | null>(null)
  const [error, setError] = useState('')

  const clearError = useCallback(() => {
    setError('')
  }, [])

  const startFlow = useCallback(
    async (provider: OAuthProvider) => {
      if (busyProviderRef.current !== null) return
      busyProviderRef.current = provider
      setError('')
      setBusyProvider(provider)
      try {
        const userId = await signInWithOAuthProvider({ provider })
        await onSuccess(userId)
      } catch (caughtError) {
        if (caughtError instanceof OAuthCancelledError) return
        Sentry.captureException(caughtError, {
          tags: { oauth_provider: provider },
        })
        setError(t('social.signInError'))
      } finally {
        busyProviderRef.current = null
        setBusyProvider(null)
      }
    },
    [onSuccess, t],
  )

  return {
    busyProvider,
    clearError,
    error,
    onPressApple: () => startFlow('apple'),
    onPressGoogle: () => startFlow('google'),
  }
}
